import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import MessageConsole from '../components/MessageConsole';
import PhoneInput from '../components/PhoneInput';
import { useAuth } from '../context/AuthContext';
import { Plus, Edit, Trash2, X, User, RefreshCw, ArrowLeft, Search, ChevronDown, ChevronUp, UserCog, MessageSquare, Mail, Phone, Calendar } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE_URL = `${BACKEND_URL}/api`;

const ManageUsers = () => {
  const { token, isAdmin, isSuperAdmin, isCalendarAdmin, user, startRunAs, isRunAsMode, canRunAs, runningUser, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const { state } = useLocation();
  const [searchParams] = useSearchParams();
  const sortByUnread = searchParams.get('sort') === 'unread';
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  
  // Unread message counts per user
  const [unreadCounts, setUnreadCounts] = useState({});
  
  // Message Console State (replaces Send Message Modal)
  const [showMessageConsole, setShowMessageConsole] = useState(false);
  const [selectedCustomerForMessage, setSelectedCustomerForMessage] = useState(null);
  
  // Legacy Send Message Modal State (for Super Admin messaging non-customers)
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messageRecipient, setMessageRecipient] = useState(null);
  const [availableChannels, setAvailableChannels] = useState({ email: true, sms: false });
  const [messageForm, setMessageForm] = useState({
    channel: 'email',
    subject: '',
    message: ''
  });
  const [sendingMessage, setSendingMessage] = useState(false);
  
  // Filter states - no default role filter
  const [filters, setFilters] = useState({
    name: '',
    email: '',
    phone: '',
    role: '',  // No default filter - show all (excluding customers)
    calendarName: '',
    accountStatus: ''
  });
  
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    password: '',
    role: 'customer',
    calendar_id: ''
  });
  
  // Calendars for selection when adding customers
  const [calendars, setCalendars] = useState([]);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      // Use getAuthHeaders() to include X-Run-As-User-Id when in Run As mode
      // This ensures we fetch users based on runningUser's context, not loginUser
      const response = await axios.get(`${API_BASE_URL}/admin/users`, {
        headers: getAuthHeaders()
      });
      // For Super Admin, exclude customers from the list
      // Customers should be managed through Calendar Admin's "Run As" feature
      const allUsers = response.data.data || [];
      const filteredUsers = isSuperAdmin() 
        ? allUsers.filter(user => user.role !== 'customer')
        : allUsers;
      setUsers(filteredUsers);
      setError('');
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Fetch unread message counts for all users
  const fetchUnreadCounts = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/messages/unread/by-sender`, {
        headers: getAuthHeaders()
      });
      
      if (response.data.success) {
        const counts = {};
        response.data.data.forEach(item => {
          counts[item.sender_id] = item.unread_count;
        });
        setUnreadCounts(counts);
      }
    } catch (err) {
      console.error('Error fetching unread counts:', err);
    }
  }, [getAuthHeaders]);

  // Fetch calendars for Calendar Admin to assign to new customers
  const fetchCalendars = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/calendars`, {
        headers: getAuthHeaders()
      });
      const calendarList = response.data.data || [];
      setCalendars(calendarList);
      
      // If only one calendar, auto-select it
      if (calendarList.length === 1) {
        setFormData(prev => ({ ...prev, calendar_id: calendarList[0].id }));
      }
    } catch (err) {
      console.error('Error fetching calendars:', err);
    }
  }, [getAuthHeaders]);

  // Reset unread count for a specific user when messages are loaded
  const handleMessagesLoaded = useCallback((userId) => {
    setUnreadCounts(prev => ({
      ...prev,
      [userId]: 0
    }));
  }, []);

  const applyFilters = useCallback(() => {
    let filtered = [...users];

    // Filter by name
    if (filters.name) {
      filtered = filtered.filter(user => 
        user.full_name?.toLowerCase().includes(filters.name.toLowerCase())
      );
    }

    // Filter by email
    if (filters.email) {
      filtered = filtered.filter(user => 
        user.email?.toLowerCase().includes(filters.email.toLowerCase())
      );
    }

    // Filter by phone
    if (filters.phone) {
      filtered = filtered.filter(user => 
        user.phone?.toLowerCase().includes(filters.phone.toLowerCase())
      );
    }

    // Filter by role (Super Admin only) - exact match
    if (isSuperAdmin() && filters.role) {
      filtered = filtered.filter(user => user.role === filters.role);
    }

    // Filter by calendar name
    if (filters.calendarName) {
      filtered = filtered.filter(user => 
        user.calendar_names?.toLowerCase().includes(filters.calendarName.toLowerCase())
      );
    }

    // Filter by account status (Super Admin only)
    if (isSuperAdmin() && filters.accountStatus) {
      filtered = filtered.filter(user => 
        user.account_status?.toLowerCase().includes(filters.accountStatus.toLowerCase())
      );
    }

    // Sort by unread messages descending if requested
    if (sortByUnread) {
      filtered.sort((a, b) => {
        const unreadA = unreadCounts[a.id] || 0;
        const unreadB = unreadCounts[b.id] || 0;
        return unreadB - unreadA;
      });
    }

    setFilteredUsers(filtered);
  }, [users, filters, isSuperAdmin, sortByUnread, unreadCounts]);

  useEffect(() => {
    if (!isAdmin()) {
      navigate('/dashboard');
      return;
    }
    fetchUsers();
    fetchAvailableChannels();
    fetchUnreadCounts();
    // Fetch calendars for Calendar Admin to assign to customers
    if (isCalendarAdmin()) {
      fetchCalendars();
    }
  }, [isAdmin, isCalendarAdmin, navigate, fetchUsers, fetchUnreadCounts, fetchCalendars]);

  // Fetch available messaging channels based on admin role and plan
  const fetchAvailableChannels = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/admin/users/messaging/channels`, {
        headers: getAuthHeaders()
      });
      if (response.data.success) {
        setAvailableChannels(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching available channels:', err);
      // Default to email only if fetch fails
      setAvailableChannels({ email: true, sms: false });
    }
  };

  // Open message console for all user types
  const openSendMessageModal = (targetUser) => {
    // Use Message Console for all users now
    // This works for: Super Admin -> Calendar Admin, Calendar Admin -> Customer, etc.
    setSelectedCustomerForMessage(targetUser);
    setShowMessageConsole(true);
  };

  // Close message console
  const closeMessageConsole = () => {
    setShowMessageConsole(false);
    setSelectedCustomerForMessage(null);
  };

  // Handle send message (legacy modal for non-customers)
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!messageRecipient) return;
    
    setSendingMessage(true);
    setError('');
    
    try {
      const response = await axios.post(
        `${API_BASE_URL}/admin/users/${messageRecipient.id}/send-message`,
        {
          subject: messageForm.channel === 'email' ? messageForm.subject : null,
          message: messageForm.message,
          channel: messageForm.channel
        },
        { headers: getAuthHeaders() }
      );
      
      if (response.data.success) {
        setSuccess(`Message sent successfully to ${messageRecipient.full_name || messageRecipient.email}`);
        setShowMessageModal(false);
        setMessageRecipient(null);
        setMessageForm({ channel: 'email', subject: '', message: '' });
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  // Add visibility change listener to refetch when page becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchUsers();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchUsers]);

  // Apply filters whenever users or filters change
  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  // Show success message from navigation state
  useEffect(() => {
    if (state?.message) {
      setSuccess(state.message);
      // Clear the message after 5 seconds
      setTimeout(() => setSuccess(''), 5000);
      // Clear navigation state
      window.history.replaceState({}, document.title);
    }
  }, [state]);

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      name: '',
      email: '',
      phone: '',
      role: '',  // Reset to "All Roles" when clearing filters
      calendarName: '',
      accountStatus: ''
    });
  };

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      const response = await axios.get(`${API_BASE_URL}/admin/users`, {
        headers: getAuthHeaders()
      });
      const allUsers = response.data.data || [];
      const filteredUsers = isSuperAdmin() 
        ? allUsers.filter(user => user.role !== 'customer')
        : allUsers;
      setUsers(filteredUsers);
      setError('');
    } catch (err) {
      console.error('Error refreshing users:', err);
      setError('Failed to refresh users');
    } finally {
      setRefreshing(false);
    }
  };

  // Navigate to create calendar page for a specific Calendar Admin
  const handleAddCalendarForAdmin = (adminUser) => {
    // Navigate to the calendars page with the admin's ID to create a calendar for them
    navigate('/admin/calendars', { 
      state: { 
        createForAdmin: {
          id: adminUser.id,
          full_name: adminUser.full_name,
          email: adminUser.email
        }
      }
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handlePhoneChange = (value) => {
    setFormData({
      ...formData,
      phone: value
    });
  };

  const openAddModal = () => {
    setEditingUser(null);
    // Auto-select calendar if only one exists (for Calendar Admin)
    const defaultCalendarId = calendars.length === 1 ? calendars[0].id : '';
    setFormData({
      full_name: '',
      email: '',
      phone: '',
      password: '',
      accepts_sms: false,
      role: isSuperAdmin() ? 'calendar_admin' : 'customer',  // Super Admin creates Calendar Admins by default
      calendar_id: defaultCalendarId
    });
    setShowModal(true);
  };

  const openEditModal = (user) => {
    navigate(`/admin/users/edit/${user.id}`);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      const userData = {
        full_name: formData.full_name,
        email: formData.email,
        phone: formData.phone || null,
        accepts_sms: formData.accepts_sms,
        role: formData.role
      };

      // Include calendar_id for customer creation (Calendar Admin only)
      if (isCalendarAdmin() && formData.role === 'customer' && formData.calendar_id) {
        userData.calendar_id = formData.calendar_id;
      }

      // Only include password if it's provided
      if (formData.password) {
        userData.password = formData.password;
      } else if (!editingUser) {
        // Password is required for new users
        setError('Password is required for new users');
        return;
      }

      if (editingUser) {
        // Update existing user
        await axios.put(
          `${API_BASE_URL}/admin/users/${editingUser.id}`,
          userData,
          { headers: getAuthHeaders() }
        );
      } else {
        // Create new user
        await axios.post(
          `${API_BASE_URL}/admin/users`,
          userData,
          { headers: getAuthHeaders() }
        );
      }

      setShowModal(false);
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save user');
    }
  };

  const handleDelete = (userId) => {
    // Navigate to delete confirmation page instead of direct delete
    navigate(`/admin/users/delete/${userId}`);
  };

  const handleRunAs = (targetUser) => {
    // Use the new canRunAs check from AuthContext
    if (!canRunAs(runningUser, targetUser)) {
      console.error('Cannot run as this user');
      return;
    }
    
    startRunAs({
      id: targetUser.id,
      role: targetUser.role,
      full_name: targetUser.full_name,
      email: targetUser.email,
      plan_type: targetUser.plan_type,
      plan_features: targetUser.plan_features
    }, window.location.pathname);
    
    // Navigate to appropriate dashboard
    window.location.href = '/dashboard';
  };

  // Toggle email verification status (Super Admin only)
  const toggleEmailVerification = async (targetUser) => {
    if (!isSuperAdmin()) return;
    
    const newStatus = !targetUser.is_email_verified;
    
    try {
      await axios.patch(
        `${API_BASE_URL}/admin/users/${targetUser.id}/email-verification`,
        { is_email_verified: newStatus },
        { headers: getAuthHeaders() }
      );
      
      // Update local state
      setUsers(prevUsers => 
        prevUsers.map(u => 
          u.id === targetUser.id 
            ? { ...u, is_email_verified: newStatus }
            : u
        )
      );
    } catch (err) {
      console.error('Error toggling email verification:', err);
      setError(err.response?.data?.detail || 'Failed to update email verification status');
      setTimeout(() => setError(''), 5000);
    }
  };

  const getRoleBadgeColor = (role) => {
    switch(role) {
      case 'super_admin':
        return 'bg-red-100 text-red-800';
      case 'calendar_admin':
        return 'bg-purple-100 text-purple-800';
      case 'customer':
        return 'bg-green-100 text-green-800';
      case 'non_registered_customer':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatRoleDisplay = (role) => {
    if (!role) return 'N/A';
    // Special display for non_registered_customer
    if (role === 'non_registered_customer') return 'Not Registered';
    return role.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-6">
          <button
            onClick={() => navigate('/dashboard')}
            className="mb-6 flex items-center text-gray-600 hover:text-gray-800 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Dashboard
          </button>
          
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-3xl font-bold text-gray-800">
                {isSuperAdmin() ? 'Manage Calendar Admins' : 'Manage Customers'}
              </h1>
              <p className="text-gray-600 mt-2">
                {isSuperAdmin() 
                  ? 'View and manage Calendar Admins. Use "Run As" to manage their customers.'
                  : 'View and manage your customers'
                }
              </p>
              {sortByUnread && (
                <p className="text-yellow-600 mt-1 text-sm font-medium flex items-center">
                  <MessageSquare className="w-4 h-4 mr-1" />
                  Sorted by unread messages (highest first)
                </p>
              )}
            </div>
            <div className="flex space-x-3">
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold px-4 py-3 rounded-md transition-colors flex items-center disabled:opacity-50"
                title="Refresh users list"
              >
                <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
              </button>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold px-4 py-3 rounded-md transition-colors flex items-center"
                title={showFilters ? "Hide filters" : "Show filters"}
              >
                {showFilters ? (
                  <ChevronUp className="w-5 h-5" />
                ) : (
                  <ChevronDown className="w-5 h-5" />
                )}
              </button>
              {/* Add User button - Super Admin can add any user, Calendar Admin can add customers */}
              {(isSuperAdmin() || isCalendarAdmin()) && (
                <button
                  onClick={openAddModal}
                  className="bg-yellow-500 hover:bg-yellow-600 text-white font-semibold px-6 py-3 rounded-md transition-colors flex items-center"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Add User
                </button>
              )}
            </div>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}

          {success && (
            <div className="mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded">
              {success}
            </div>
          )}

          {/* Filters Section */}
          {showFilters && (
            <div className="bg-white rounded-lg shadow p-4 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                <Search className="w-5 h-5 mr-2" />
                Filter Users
              </h3>
              <button
                onClick={clearFilters}
                className="text-sm text-gray-600 hover:text-gray-800 underline"
              >
                Clear All Filters
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  placeholder="Filter by name..."
                  value={filters.name}
                  onChange={(e) => handleFilterChange('name', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="text"
                  placeholder="Filter by email..."
                  value={filters.email}
                  onChange={(e) => handleFilterChange('email', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Phone</label>
                <input
                  type="text"
                  placeholder="Filter by phone..."
                  value={filters.phone}
                  onChange={(e) => handleFilterChange('phone', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                />
              </div>
              {isSuperAdmin() && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Role</label>
                  <select
                    value={filters.role}
                    onChange={(e) => handleFilterChange('role', e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-transparent bg-white"
                  >
                    <option value="">All Roles</option>
                    <option value="calendar_admin">Calendar Admin</option>
                    <option value="non_registered_customer">Not Registered</option>
                    <option value="super_admin">Super Admin</option>
                  </select>
                </div>
              )}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Calendar Name</label>
                <input
                  type="text"
                  placeholder="Filter by calendar..."
                  value={filters.calendarName}
                  onChange={(e) => handleFilterChange('calendarName', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                />
              </div>
              {isSuperAdmin() && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Account Status</label>
                  <input
                    type="text"
                    placeholder="Filter by status..."
                    value={filters.accountStatus}
                    onChange={(e) => handleFilterChange('accountStatus', e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  />
                </div>
              )}
            </div>
            <div className="mt-3 text-sm text-gray-600">
              Showing {filteredUsers.length} of {users.length} user{users.length !== 1 ? 's' : ''}
            </div>
          </div>
          )}

          {/* Users Table */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Phone
                    </th>
                    {/* SPACE OPTIMIZATION (Jan 2026): Role column hidden for Super Admin
                        since this page only shows Calendar Admins */}
                    {isSuperAdmin() && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Plan
                      </th>
                    )}
                    {isSuperAdmin() && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Verified
                      </th>
                    )}
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Calendar Name
                    </th>
                    {/* SPACE OPTIMIZATION (Jan 2026): Status column hidden for Super Admin
                        - Active status indicated by green avatar circle
                        - Inactive status indicated by red avatar circle */}
                    {isCalendarAdmin() && (
                      <>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Appointment Count
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Unread Messages
                        </th>
                      </>
                    )}
                    {/* SPACE OPTIMIZATION (Jan 2026): Unread Messages column hidden for Super Admin
                        - Unread count shown as badge on Message icon in Actions column */}
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredUsers.length === 0 ? (
                    <tr>
                      <td colSpan={isSuperAdmin() ? 7 : 7} className="px-6 py-8 text-center text-gray-500">
                        {users.length === 0 ? 'No users found' : 'No users match your filters'}
                      </td>
                    </tr>
                  ) : (
                    filteredUsers.map((user) => (
                      <tr key={user.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10">
                              {/* SPACE OPTIMIZATION (Jan 2026): Avatar circle color indicates status
                                  - Light green: Active
                                  - Light red: Inactive
                                  This replaces the separate Status column */}
                              <div className={`h-10 w-10 rounded-full flex items-center justify-center ${
                                user.account_status === 'Active' || user.is_active !== false
                                  ? 'bg-green-100'
                                  : 'bg-red-100'
                              }`}>
                                <User className={`h-5 w-5 ${
                                  user.account_status === 'Active' || user.is_active !== false
                                    ? 'text-green-600'
                                    : 'text-red-600'
                                }`} />
                              </div>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {user.full_name || 'N/A'}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-900">{user.email}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-900">{user.phone || 'N/A'}</span>
                        </td>
                        {/* SPACE OPTIMIZATION (Jan 2026): Role column removed for Super Admin */}
                        {isSuperAdmin() && (
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-sm text-gray-900 capitalize">
                              {user.role === 'customer' ? '' : (user.plan_type || 'Free')}
                            </span>
                          </td>
                        )}
                        {isSuperAdmin() && (
                          <td className="px-6 py-4 whitespace-nowrap">
                            <button
                              onClick={() => toggleEmailVerification(user)}
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium cursor-pointer transition-colors ${
                                user.is_email_verified
                                  ? 'bg-green-100 text-green-800 hover:bg-green-200'
                                  : 'bg-red-100 text-red-800 hover:bg-red-200'
                              }`}
                              title={`Click to ${user.is_email_verified ? 'unverify' : 'verify'} email`}
                              data-testid={`toggle-verification-${user.id}`}
                            >
                              {user.is_email_verified ? '✓ Verified' : '✗ Unverified'}
                            </button>
                          </td>
                        )}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-900">{user.calendar_names || 'None'}</span>
                        </td>
                        {/* SPACE OPTIMIZATION (Jan 2026): Status column removed for Super Admin */}
                        {isCalendarAdmin() && (
                          <>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="text-sm text-gray-900">{user.appointment_count || 0}</span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {(unreadCounts[user.id] || 0) > 0 ? (
                                <span className="inline-flex items-center justify-center w-7 h-7 text-xs font-bold text-white bg-red-500 rounded-full">
                                  {unreadCounts[user.id] > 99 ? '99+' : unreadCounts[user.id]}
                                </span>
                              ) : (
                                <span className="text-sm text-gray-400">0</span>
                              )}
                            </td>
                          </>
                        )}
                        {/* SPACE OPTIMIZATION (Jan 2026): Unread Messages column removed for Super Admin
                            - Badge now shown on Message icon in Actions */}
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          {/* SPACE OPTIMIZATION (Jan 2026): Message icon with unread badge
                              Badge replaces the separate Unread Messages column */}
                          <button
                            onClick={() => openSendMessageModal(user)}
                            className="text-green-600 hover:text-green-900 mr-4 relative"
                            title={`Send message to ${user.full_name}${(unreadCounts[user.id] || 0) > 0 ? ` (${unreadCounts[user.id]} unread)` : ''}`}
                            data-testid={`send-message-${user.id}`}
                          >
                            <MessageSquare className="w-5 h-5" />
                            {/* Unread count badge - shown on message icon (30% smaller) */}
                            {(unreadCounts[user.id] || 0) > 0 && (
                              <span className="absolute -top-1.5 -right-1.5 inline-flex items-center justify-center w-3.5 h-3.5 text-[10px] font-bold text-white bg-red-500 rounded-full">
                                {unreadCounts[user.id] > 99 ? '99+' : unreadCounts[user.id]}
                              </span>
                            )}
                          </button>
                          {/* View Calendar(s) button - show for Calendar Admins WITH calendars (Super Admin only) */}
                          {isSuperAdmin() && user.role === 'calendar_admin' && user.calendar_ids && user.calendar_ids.length > 0 && (
                            <button
                              onClick={() => {
                                if (user.calendar_ids.length === 1) {
                                  // Single calendar - open directly in CalendarView
                                  navigate(`/calendar/${user.calendar_ids[0]}`);
                                } else {
                                  // Multiple calendars - open Manage Calendars with Run As
                                  startRunAs(user);
                                  navigate('/admin/calendars');
                                }
                              }}
                              className="text-purple-600 hover:text-purple-900 mr-4"
                              title={user.calendar_ids.length === 1 
                                ? `Open ${user.full_name}'s calendar` 
                                : `View ${user.full_name}'s ${user.calendar_ids.length} calendars`}
                              data-testid={`view-calendar-${user.id}`}
                            >
                              <Calendar className="w-5 h-5" />
                            </button>
                          )}
                          {/* Add Calendar button - show for Calendar Admins without calendars (Super Admin only) */}
                          {isSuperAdmin() && user.role === 'calendar_admin' && (!user.calendar_ids || user.calendar_ids.length === 0) && (
                            <button
                              onClick={() => handleAddCalendarForAdmin(user)}
                              className="text-yellow-600 hover:text-yellow-900 mr-4"
                              title={`Add calendar for ${user.full_name}`}
                              data-testid={`add-calendar-${user.id}`}
                            >
                              <Calendar className="w-5 h-5" />
                            </button>
                          )}
                          {/* Run As button - show if current runningUser can run as this user and user is not non_registered */}
                          {canRunAs(runningUser, user) && user.role !== 'non_registered_customer' && (
                            <button
                              onClick={() => handleRunAs(user)}
                              className="text-orange-600 hover:text-orange-900 mr-4"
                              title={`Run as ${user.full_name}`}
                              data-testid={`run-as-${user.id}`}
                            >
                              <UserCog className="w-5 h-5" />
                            </button>
                          )}
                          <button
                            onClick={() => openEditModal(user)}
                            className="text-blue-600 hover:text-blue-900 mr-4"
                            title="Edit user"
                          >
                            <Edit className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handleDelete(user.id)}
                            className="text-red-600 hover:text-red-900"
                            title="Delete user"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-800">
                  {editingUser ? 'Edit User' : 'Add New User'}
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {error && (
                <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded text-sm">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="full_name"
                    value={formData.full_name}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                    required
                    disabled={!!editingUser}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone
                  </label>
                  <PhoneInput
                    value={formData.phone}
                    onChange={handlePhoneChange}
                    className="w-full"
                    data-testid="add-user-phone"
                  />
                  {/* Accept SMS Checkbox */}
                  <label className="flex items-start mt-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.accepts_sms}
                      onChange={(e) => setFormData({...formData, accepts_sms: e.target.checked})}
                      disabled={!formData.phone}
                      className="mt-0.5 h-4 w-4 text-yellow-500 border-gray-300 rounded focus:ring-yellow-500 disabled:opacity-50"
                    />
                    <span className={`ml-2 text-sm ${formData.phone ? 'text-gray-700' : 'text-gray-400'}`}>
                      <MessageSquare className="w-4 h-4 inline mr-1" />
                      User accepts SMS notifications
                    </span>
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Password {editingUser ? '(leave blank to keep current)' : '*'}
                  </label>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                    required={!editingUser}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Role *
                  </label>
                  {isCalendarAdmin() ? (
                    // Calendar Admin can only create Customers
                    <div className="w-full px-4 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-700">
                      Customer
                      <input type="hidden" name="role" value="customer" />
                    </div>
                  ) : (
                    // Super Admin can select role
                    <select
                      name="role"
                      value={formData.role}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                      required
                    >
                      <option value="calendar_admin">Calendar Admin</option>
                      <option value="customer">Customer</option>
                    </select>
                  )}
                </div>

                {/* Calendar Selection - Only for Calendar Admin creating customers */}
                {isCalendarAdmin() && !editingUser && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Calendar className="w-4 h-4 inline mr-1" />
                      Assign to Calendar {calendars.length > 1 ? '' : '(Auto-assigned)'}
                    </label>
                    {calendars.length === 0 ? (
                      <div className="w-full px-4 py-2 border border-gray-300 rounded-md bg-yellow-50 text-yellow-700 text-sm">
                        No calendars found. Please create a calendar first.
                      </div>
                    ) : calendars.length === 1 ? (
                      <div className="w-full px-4 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-700">
                        {calendars[0].name}
                        <input type="hidden" name="calendar_id" value={calendars[0].id} />
                      </div>
                    ) : (
                      <select
                        name="calendar_id"
                        value={formData.calendar_id}
                        onChange={handleChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                      >
                        <option value="">-- Select Calendar --</option>
                        {calendars.map((calendar) => (
                          <option key={calendar.id} value={calendar.id}>
                            {calendar.name}
                          </option>
                        ))}
                      </select>
                    )}
                    <p className="text-sm text-gray-500 mt-1">
                      Customer will be registered to this calendar for booking
                    </p>
                  </div>
                )}

                <div className="flex justify-end space-x-3 pt-4 border-t">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 bg-yellow-500 hover:bg-yellow-600 text-white rounded-md transition-colors"
                  >
                    {editingUser ? 'Update User' : 'Add User'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Send Message Modal */}
      {showMessageModal && messageRecipient && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-800">
                  Send Message
                </h2>
                <button
                  onClick={() => {
                    setShowMessageModal(false);
                    setMessageRecipient(null);
                    setError('');
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Recipient Info */}
              <div className="mb-4 p-3 bg-gray-50 rounded-md">
                <div className="flex items-center">
                  <div className="flex-shrink-0 h-10 w-10">
                    <div className="h-10 w-10 rounded-full bg-yellow-100 flex items-center justify-center">
                      <User className="h-5 w-5 text-yellow-600" />
                    </div>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-900">{messageRecipient.full_name || 'N/A'}</p>
                    <div className="flex items-center text-xs text-gray-500 space-x-3">
                      {messageRecipient.email && (
                        <span className="flex items-center">
                          <Mail className="w-3 h-3 mr-1" />
                          {messageRecipient.email}
                        </span>
                      )}
                      {messageRecipient.phone && (
                        <span className="flex items-center">
                          <Phone className="w-3 h-3 mr-1" />
                          {messageRecipient.phone}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {error && (
                <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded text-sm">
                  {error}
                </div>
              )}

              <form onSubmit={handleSendMessage} className="space-y-4">
                {/* Channel Selection - Show if SMS is available, otherwise auto-select email */}
                {(isSuperAdmin() || availableChannels.sms) ? (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Send via *
                    </label>
                    <div className="flex space-x-4">
                      <label className={`flex items-center p-3 border rounded-md cursor-pointer transition-colors ${
                        messageForm.channel === 'email' 
                          ? 'border-yellow-500 bg-yellow-50' 
                          : 'border-gray-300 hover:border-gray-400'
                      } ${!messageRecipient.email ? 'opacity-50 cursor-not-allowed' : ''}`}>
                        <input
                          type="radio"
                          name="channel"
                          value="email"
                          checked={messageForm.channel === 'email'}
                          onChange={(e) => setMessageForm({...messageForm, channel: e.target.value})}
                          disabled={!messageRecipient.email}
                          className="sr-only"
                        />
                        <Mail className="w-5 h-5 mr-2 text-blue-600" />
                        <span className="text-sm font-medium">Email</span>
                      </label>
                      <label className={`flex items-center p-3 border rounded-md cursor-pointer transition-colors ${
                        messageForm.channel === 'sms' 
                          ? 'border-yellow-500 bg-yellow-50' 
                          : 'border-gray-300 hover:border-gray-400'
                      } ${(!messageRecipient.phone || !messageRecipient.accepts_sms) ? 'opacity-50 cursor-not-allowed' : ''}`}>
                        <input
                          type="radio"
                          name="channel"
                          value="sms"
                          checked={messageForm.channel === 'sms'}
                          onChange={(e) => setMessageForm({...messageForm, channel: e.target.value})}
                          disabled={!messageRecipient.phone || !messageRecipient.accepts_sms}
                          className="sr-only"
                        />
                        <Phone className="w-5 h-5 mr-2 text-green-600" />
                        <span className="text-sm font-medium">SMS</span>
                      </label>
                    </div>
                    {!messageRecipient.email && messageForm.channel === 'email' && (
                      <p className="mt-1 text-xs text-red-500">User has no email address</p>
                    )}
                    {!messageRecipient.phone && messageForm.channel === 'sms' && (
                      <p className="mt-1 text-xs text-red-500">User has no phone number</p>
                    )}
                    {messageRecipient.phone && !messageRecipient.accepts_sms && (
                      <p className="mt-1 text-xs text-orange-500">User has not consented to SMS communications</p>
                    )}
                  </div>
                ) : (
                  <div className="text-sm text-gray-600 bg-blue-50 p-3 rounded-md">
                    <Mail className="w-4 h-4 inline mr-2 text-blue-600" />
                    Sending via Email
                    {!availableChannels.sms && isCalendarAdmin() && (
                      <span className="block mt-1 text-xs text-gray-500">
                        (SMS available with upgraded plans)
                      </span>
                    )}
                  </div>
                )}

                {/* Subject - Only for email */}
                {messageForm.channel === 'email' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Subject *
                    </label>
                    <input
                      type="text"
                      value={messageForm.subject}
                      onChange={(e) => setMessageForm({...messageForm, subject: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                      placeholder="Enter email subject..."
                      required={messageForm.channel === 'email'}
                    />
                  </div>
                )}

                {/* Message Body */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Message *
                    {messageForm.channel === 'sms' && (
                      <span className="text-xs text-gray-500 ml-2">
                        ({messageForm.message.length}/160 characters)
                      </span>
                    )}
                  </label>
                  <textarea
                    value={messageForm.message}
                    onChange={(e) => setMessageForm({...messageForm, message: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                    rows={messageForm.channel === 'sms' ? 3 : 5}
                    placeholder={messageForm.channel === 'sms' ? 'Enter your SMS message...' : 'Enter your email message...'}
                    maxLength={messageForm.channel === 'sms' ? 160 : undefined}
                    required
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Available variables: <code className="bg-gray-100 px-1">&customer-name</code>, <code className="bg-gray-100 px-1">&customer-email</code>
                  </p>
                </div>

                <div className="flex justify-end space-x-3 pt-4 border-t">
                  <button
                    type="button"
                    onClick={() => {
                      setShowMessageModal(false);
                      setMessageRecipient(null);
                      setError('');
                    }}
                    className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={sendingMessage || (messageForm.channel === 'email' && !messageRecipient.email) || (messageForm.channel === 'sms' && (!messageRecipient.phone || !messageRecipient.accepts_sms))}
                    className="px-6 py-2 bg-yellow-500 hover:bg-yellow-600 text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    {sendingMessage ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <MessageSquare className="w-4 h-4 mr-2" />
                        Send Message
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Message Console (slide-out panel for customers) */}
      <MessageConsole
        customer={selectedCustomerForMessage}
        isOpen={showMessageConsole}
        onClose={closeMessageConsole}
        onMessagesLoaded={() => selectedCustomerForMessage && handleMessagesLoaded(selectedCustomerForMessage.id)}
      />

      <Footer />
    </div>
  );
};

export default ManageUsers;
